<!--
  Usage
  <label for="noDecimals">Allows NO decimals:</label><br>
  <positive-numeric-input id="noDecimals" name="d1" v-model="d1" placeholder="Insert value"></positive-numeric-input>
  {{d1}}
  <br>
  <label for="decimal1">Allows 1 decimal:</label><br>
  <positive-numeric-input id="decimal1" name="d2" v-model="d2" decimals :no-decimals="1" placeholder="Insert value"></positive-numeric-input>
  {{d2}}
  <br>
  <label for="decimal5">Allows 5 decimals with thousands separator:</label><br>
  <positive-numeric-input id="decimal5" name="d3" v-model="d3" decimals :no-decimals="5" thousands-separator placeholder="Insert value"></positive-numeric-input>
  {{d3}}
  <br>

-->
<template>
  <v-text-field
    v-if="visible"
    type="text"
    :placeholder="placeholder"
    :value="formattedValue"
    @focus="visible = false"
    :name="name"
    :tabindex="tabindex"
    :readonly="isReadonly"
  >
    <template v-slot:label>{{ $t(label) }} </template>
  </v-text-field>
  <v-text-field
    v-else
    type="number"
    :step="step"
    :placeholder="placeholder"
    v-model="value"
    min="0"
    @blur="visible = true"
    @keypress="onKeypress"
    @input="onInput"
    :name="name"
    :tabindex="tabindex"
    :readonly="isReadonly"
    :maxlength="maxLength"
    :max="maxValue"
  >
    <template v-slot:label>{{ $t(label) }} </template>
  </v-text-field>
</template>

<script>
export default {
  name: 'positive-numeric-input',
  props: {
    name: String,
    label: String,
    placeholder: String,
    tabindex: String,
    isReadonly: Boolean,
    decimals: {
      type: Boolean,
      default: false
    },
    thousandsSeparator: {
      type: Boolean,
      default: false
    },
    noDecimals: {
      type: Number,
      validator: (value) => {
        return value >= 1
      }
    },
    initialValue: {
      type: Number,
      validator: (value) => {
        return !value || value >= 0
      }
    },
    maxValue: {
      type: Number,
      validator: (value) => {
        return !value || value >= 0
      },
      default: 999999
    },
    maxLength: {
      type: Number,
      validator: (value) => {
        return !value || value >= 0
      },
      default: 6
    }
  },
  model: {
    prop: 'initialValue'
  },
  watch: {
    initialValue: function (newVal) {
      if (this.value !== newVal) {
        this.value = newVal
        this.enforceConstraints()
        this.$emit('input', parseFloat(this.value))
      }
    }
  },
  data() {
    return {
      value: this.initialValue,
      formattedValue: null,
      visible: true
    }
  },
  created() {
    this.enforceConstraints()
    this.$emit('input', parseFloat(this.value))
  },
  computed: {
    step() {
      if (!this.decimals) {
        return 0
      } else if (!Number.isInteger(this.noDecimals)) {
        return 'any'
      } else {
        return 1.0 / 10 ** this.noDecimals
      }
    }
  },
  methods: {
    enforceConstraints() {
      let amount = parseFloat(this.value)
      if (Number.isNaN(amount)) {
        this.formattedValue = null
        return
      }
      const [whole, decimals] = amount.toString().split('.')

      if (!this.decimals) {
        // If no decimals allowed, use just the whole number
        amount = parseInt(whole)
      } else if (
        Number.isInteger(this.noDecimals) &&
        decimals &&
        decimals.length > this.noDecimals
      ) {
        // If we should enforce the number of decimals
        amount = amount.toFixed(this.noDecimals)
      }

      amount = Math.min(amount, this.maxValue)
      this.value = amount
      if (this.decimals && this.thousandsSeparator) {
        const [whole, decimals] = amount.toString().split('.')
        this.formattedValue =
          whole.replace(/\B(?=(\d{3})+(?!\d))/g, ',') +
          (decimals ? '.' + decimals : '')
      }

      if (!this.decimals && this.thousandsSeparator) {
        const [whole, decimals] = amount.toString().split('.')
        this.formattedValue =
          whole.replace(/\B(?=(\d{3})+(?!\d))/g, ',') +
          (decimals ? '.' + decimals : '')
      }

      if (!this.decimals && !this.thousandsSeparator) {
        this.formattedValue = amount.toString()
      }
    },
    onInput() {
      this.enforceConstraints()
      const val = parseFloat(this.value)
      this.$emit('input', Number.isNaN(val) ? null : val)
    },
    onKeypress(e) {
      e = e || window.event
      const keyCode = e.keyCode || e.which
      const isDigit =
        (keyCode >= 48 && keyCode <= 57) || (keyCode >= 96 && keyCode <= 105)
      const isDot = keyCode === 46

      // forcing the maximum length
      if (e.target.value.length >= e.target.maxLength) {
        e.target.value = e.target.value.slice(0, e.target.maxLength)
        e.preventDefault()
        return
      }

      // Allow digits and dot (only if we want decimals)
      if (isDigit || (isDot && this.decimals)) {
        return true
      }

      // Prevent any other chars
      e.preventDefault()
      return false
    }
  }
}
</script>